import { voiceDiagnostic, diagnosticErrorType } from './voiceDiagnostics';

export const MICROPHONE_TEST_SECONDS = 15;
const MAX_RECORDING_BYTES = 5 * 1024 * 1024;
const FORMATS = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];

export type MicrophoneCapabilities = {
    secure: boolean;
    capture: boolean;
    recorder: boolean;
    meter: boolean;
    speechRecognition: boolean;
    formats: string[];
};
export type MicrophoneTestState = {
    capabilities?: MicrophoneCapabilities;
    phase: 'idle' | 'requesting' | 'ready' | 'recording' | 'stopping' | 'playing';
    level: number;
    seconds: number;
    bytes: number;
    format?: string;
    audioState?: AudioContextState;
    error?: string;
};

export function createMicrophoneTestController(browser: () => Window & typeof globalThis = () => window,
    limits = { seconds: MICROPHONE_TEST_SECONDS, bytes: MAX_RECORDING_BYTES }) {
    const serverSnapshot: MicrophoneTestState = { phase: 'idle', level: 0, seconds: 0, bytes: 0 };
    let state = serverSnapshot;
    const listeners = new Set<() => void>();
    let generation = 0;
    let stream: MediaStream | undefined;
    let context: AudioContext | undefined;
    let recorder: MediaRecorder | undefined;
    let meterTimer: ReturnType<typeof setInterval> | undefined;
    let limitTimer: ReturnType<typeof setTimeout> | undefined;
    let recordingUrl: string | undefined;
    let recording: Blob | undefined;
    let audio: HTMLAudioElement | undefined;
    const update = (patch: Partial<MicrophoneTestState>) => {
        if (patch.phase && patch.phase !== state.phase) voiceDiagnostic('microphone-phase', { phase: patch.phase });
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };
    const report = (message: string, failure: unknown) => {
        voiceDiagnostic('microphone-error', { errorType: diagnosticErrorType(failure) });
        console.error('[microphone-test]', message, failure);
        update({ error: `${message}: ${failure instanceof Error ? failure.message : String(failure)}` });
    };
    const releaseCapture = () => {
        clearInterval(meterTimer);
        clearTimeout(limitTimer);
        stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
        stream = undefined;
        const closing = context;
        context = undefined;
        if (closing) closing.onstatechange = null;
        if (closing && closing.state !== 'closed') void closing.close().catch(error => report('Could not close audio context', error));
    };
    const stopPlayback = () => {
        if (audio) {
            audio.onended = null;
            audio.onerror = null;
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
            audio = undefined;
        }
    };
    const discardRecording = () => {
        stopPlayback();
        if (recordingUrl) browser().URL.revokeObjectURL(recordingUrl);
        recordingUrl = undefined;
        recording = undefined;
    };
    const check = () => {
        const host = browser();
        const capabilities = {
            secure: host.isSecureContext,
            capture: !!host.navigator.mediaDevices?.getUserMedia,
            recorder: typeof host.MediaRecorder === 'function',
            meter: typeof host.AudioContext === 'function',
            speechRecognition: 'SpeechRecognition' in host || 'webkitSpeechRecognition' in host,
            formats: typeof host.MediaRecorder === 'function' ? FORMATS.filter(format => host.MediaRecorder.isTypeSupported(format)) : [],
        };
        update({ capabilities });
        return capabilities;
    };
    const stop = () => {
        if (state.phase === 'recording' && recorder?.state === 'recording') {
            update({ phase: 'stopping', level: 0 });
            recorder.stop();
            releaseCapture();
        } else if (state.phase !== 'stopping') {
            generation++;
            releaseCapture();
            stopPlayback();
            update({ phase: 'idle', level: 0, audioState: undefined });
        }
    };
    const enable = async () => {
        if (state.phase !== 'idle') return;
        const capabilities = check();
        if (!capabilities.secure || !capabilities.capture || !capabilities.recorder || !capabilities.meter) {
            update({ error: 'Microphone test requires trusted HTTPS, microphone capture, MediaRecorder and AudioContext support.' });
            return;
        }
        const request = ++generation;
        update({ phase: 'requesting', error: undefined });
        try {
            const host = browser();
            // Resume in the initiating user gesture, before waiting for a permission prompt.
            context = new host.AudioContext();
            context.onstatechange = () => {
                if (request === generation && context) update({ audioState: context.state });
            };
            const resumed = context.resume();
            const requested = host.navigator.mediaDevices.getUserMedia({ audio: true, video: false }).then(captured => {
                if (request !== generation) captured.getTracks().forEach(track => track.stop());
                else stream = captured;
                return captured;
            });
            const [, captured] = await Promise.all([resumed, requested]);
            if (request !== generation || !context) return;
            const analyser = context.createAnalyser();
            analyser.fftSize = 256;
            context.createMediaStreamSource(captured).connect(analyser);
            const samples = new Float32Array(analyser.fftSize);
            captured.getAudioTracks().forEach(track => {
                track.onended = () => {
                    stop();
                    update({ error: 'Microphone disconnected or permission was revoked.' });
                };
            });
            meterTimer = setInterval(() => {
                analyser.getFloatTimeDomainData(samples);
                const rms = Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length);
                update({ level: Math.min(100, Math.round(rms * 100)) });
            }, 100);
            update({ phase: 'ready', audioState: context.state });
        } catch (failure) {
            if (request !== generation) return;
            generation++;
            releaseCapture();
            update({ phase: 'idle', level: 0, audioState: undefined });
            report('Could not enable microphone', failure);
        }
    };
    const record = () => {
        if (state.phase !== 'ready' || !stream) return;
        const host = browser();
        const request = generation;
        try {
            discardRecording();
            const mimeType = state.capabilities?.formats[0];
            const current = new host.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            recorder = current;
            const chunks: Blob[] = [];
            let bytes = 0;
            const started = Date.now();
            current.ondataavailable = event => {
                if (request !== generation || !event.data.size) return;
                bytes += event.data.size;
                if (bytes > limits.bytes) {
                    update({ error: `Recording exceeded the ${limits.bytes / (1024 * 1024)} MiB limit; recording discarded.` });
                    dispose(false);
                    return;
                }
                chunks.push(event.data);
                update({ bytes, seconds: (Date.now() - started) / 1000 });
            };
            current.onerror = () => {
                update({ error: 'Audio recording failed; recording discarded.' });
                dispose(false);
            };
            current.onstop = () => {
                if (request !== generation) return;
                releaseCapture();
                recorder = undefined;
                const blob = new host.Blob(chunks, { type: current.mimeType || chunks[0]?.type });
                if (blob.size) {
                    recording = blob;
                    recordingUrl = host.URL.createObjectURL(blob);
                    update({ phase: 'idle', level: 0, audioState: undefined, bytes: blob.size, format: blob.type,
                        seconds: (Date.now() - started) / 1000 });
                } else {
                    update({ phase: 'idle', level: 0, audioState: undefined, error: 'The recording was empty. Enable the microphone and try again.' });
                }
            };
            current.start(250);
            update({ phase: 'recording', error: undefined, seconds: 0, bytes: 0, format: current.mimeType });
            limitTimer = setTimeout(stop, limits.seconds * 1000);
        } catch (failure) {
            dispose(false);
            report('Could not start recording', failure);
        }
    };
    const play = async () => {
        if (state.phase !== 'idle' || !recordingUrl) return;
        const request = generation;
        const host = browser();
        const current = new host.Audio(recordingUrl);
        audio = current;
        current.onended = () => { stopPlayback(); update({ phase: 'idle' }); };
        current.onerror = () => {
            stopPlayback();
            update({ phase: 'idle', error: 'Recording playback failed. This browser may not support its recording format.' });
        };
        update({ phase: 'playing', error: undefined });
        try { await current.play(); }
        catch (failure) {
            if (request !== generation || audio !== current) return;
            stopPlayback();
            update({ phase: 'idle' });
            report('Could not play recording', failure);
        }
    };
    function dispose(clearError = true) {
        generation++;
        if (recorder) {
            recorder.ondataavailable = null;
            recorder.onstop = null;
            recorder.onerror = null;
            if (recorder.state !== 'inactive') recorder.stop();
            recorder = undefined;
        }
        releaseCapture();
        discardRecording();
        update({ phase: 'idle', level: 0, seconds: 0, bytes: 0, format: undefined, audioState: undefined,
            ...(clearError ? { error: undefined } : {}) });
    }
    return {
        getSnapshot: () => state,
        getServerSnapshot: () => serverSnapshot,
        getRecording: () => recording,
        subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
        check, enable, record, stop, play, dispose,
    };
}

export function microphoneTestSummary(state: MicrophoneTestState) {
    const c = state.capabilities;
    return [
        c ? `Secure origin: ${c.secure ? 'yes' : 'NO'} | Capture: ${c.capture ? 'yes' : 'no'} | Recorder: ${c.recorder ? 'yes' : 'no'} | Meter: ${c.meter ? 'yes' : 'no'}` : 'Checking browser capabilities...',
        c ? `Speech recognition API exposed: ${c.speechRecognition ? 'yes (not verified)' : 'no'}.` : '',
        c ? `Formats: ${c.formats.join(', ') || 'browser default only'}` : '',
        `Microphone: ${state.phase} | Level: ${state.level}% RMS`,
        state.audioState ? `Audio context: ${state.audioState}` : '',
        `Clip: ${state.seconds.toFixed(1)}s | ${state.bytes} bytes${state.format ? ` | ${state.format}` : ''}`,
        state.error ?? 'Audio stays in browser memory. No upload or transcription.',
    ].filter(Boolean).join('\n');
}
